import { Download, FileText, X, ZoomIn } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TwoPagePdfViewer } from "../TwoPagePdfViewer/TwoPagePdfViewer";
import { downloadDocumentFile } from "../../utils/downloadDocumentFile";
import isPdfFile from "../../utils/isPdfFile";
import { PublicPortalDocumentPreviewSkeleton } from "./PublicPortalSkeleton";

const PublicDocumentPreviewCard = ({
  title,
  url,
  fileType = null,
  alt,
  allowDownload = false,
  downloadFileName,
}) => {
  const { t } = useTranslation("publicPortal");
  const isPdf = isPdfFile(url, fileType);
  const [isFullPreviewOpen, setIsFullPreviewOpen] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(
    () => Boolean(url) && !isPdf,
  );
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!url || isPdf) {
      setIsImageLoading(false);
      return;
    }
    setIsImageLoading(true);
  }, [url, isPdf]);

  useEffect(() => {
    if (!isFullPreviewOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFullPreviewOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullPreviewOpen]);

  if (!url) return null;

  const handleDownload = async () => {
    if (!allowDownload || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadDocumentFile(url, downloadFileName || "document");
    } finally {
      setIsDownloading(false);
    }
  };

  const renderPreview = (heightClassName, imageClassName) =>
    isPdf ? (
      <TwoPagePdfViewer
        pdfUrl={url}
        showZoom={false}
        paged
        heightClassName={heightClassName}
        viewerClassName="h-full min-w-0 max-w-full"
      />
    ) : (
      <img
        src={url}
        alt={alt}
        className={imageClassName}
        onLoad={() => setIsImageLoading(false)}
        onError={() => setIsImageLoading(false)}
      />
    );

  const downloadButton = (size = "sm") =>
    allowDownload ? (
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDownloading}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-cfCardBorder bg-cfSecondary font-semibold text-cfHeadingText hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${
          size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"
        }`}
      >
        <Download aria-hidden="true" size={size === "sm" ? 14 : 16} />
        {t("verification.download")}
      </button>
    ) : null;

  return (
    <>
      <div className="flex min-h-0 min-w-0 w-full max-w-full flex-col overflow-hidden rounded-xl border border-black p-3 shadow-sm sm:p-4">
        <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center space-x-2 sm:space-x-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
              <FileText className="h-5 w-5 text-orange-500" />
            </div>
            <h3 className="truncate text-base font-semibold text-gray-900 sm:text-lg">
              {title}
            </h3>
          </div>
          {downloadButton("sm")}
        </div>

        <div className="relative mt-3 min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg bg-gray-200">
          <div className="h-full min-h-0 w-full min-w-0 max-w-full overflow-hidden rounded-lg">
            {isImageLoading && <PublicPortalDocumentPreviewSkeleton />}
            {renderPreview(
              "h-full",
              `mx-auto max-h-full max-w-full w-full object-contain ${
                isImageLoading ? "hidden" : "block"
              }`,
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsFullPreviewOpen(true)}
            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-800 shadow-md hover:bg-white"
            aria-label={t("verification.fullPreview")}
          >
            <ZoomIn aria-hidden="true" className="h-5 w-5" strokeWidth={2.25} />
          </button>
        </div>
      </div>

      {isFullPreviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-publicPortal-overlay p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={() => setIsFullPreviewOpen(false)}
        >
          <div
            className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-cfSecondary shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-cfCardBorder px-5 py-4 sm:px-7">
              <h2 className="m-0 min-w-0 truncate text-xl font-bold text-cfHeadingText">
                {title}
              </h2>
              <div className="flex shrink-0 items-center gap-2">
                {downloadButton("md")}
                <button
                  type="button"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-cfSearchContainer text-cfGlobalText"
                  onClick={() => setIsFullPreviewOpen(false)}
                  aria-label={t("verification.close")}
                >
                  <X aria-hidden="true" size={20} />
                </button>
              </div>
            </div>

            <div
              className={`min-h-0 min-w-0 flex-1 p-3 sm:p-6 ${
                isPdf ? "overflow-hidden" : "overflow-auto"
              }`}
            >
              {renderPreview(
                "h-[75vh]",
                "mx-auto max-h-[75vh] max-w-full w-full object-contain",
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicDocumentPreviewCard;
